import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import useEncryption from "../../../services/useEncryption";
import TextField from "@mui/material/TextField";
import { Autocomplete } from "@mui/material";
import {
  billingInformationApiAdmin,
  createAuditLogs,
  getBillingInformationApi,
} from "../../../api/providerApi/providerApi";
import { stepTwoSchema } from "../../../services/formValidator/providerSignupValidator";
import { Country, State, City } from "country-state-city";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import useHeaders from "../../../helpers/useHeaders";
import { useSelector } from "react-redux";
import { selectLoc } from "../../../redux/slice/locSlice";

export const Steptwo = ({ providerId }) => {
  const { encryptData, decryptData } = useEncryption();
  const [loading, setLoading] = useState(true);
  const stripe = useStripe();
  const elements = useElements();
  const [billingData, setBillingData] = useState({});
  const headers = useHeaders();
  const { loc } = useSelector(selectLoc);
  const [buttonText, setButtonText] = useState("Saved");

  const initialValues = {
    accountCardEntry: "",
    emailForReceipts: "",
    nameOnAccount: "",
    postal_code: "",
    billingAddress: {
      contactName: "",
      billingEmail: "",
      officeName: "",
      streetAddress: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
  };

  const validationSchema = stepTwoSchema;

  const onSubmit = async (values) => {
    try {
      let token;
      if (!billingData.last4) {
        if (!stripe || !elements) {
          toast.error("Stripe has not loaded yet.");
          return;
        }

        // Validate Stripe Elements
        const cardNumber = elements.getElement(CardNumberElement);
        const cardExpiry = elements.getElement(CardExpiryElement);
        const cardCvc = elements.getElement(CardCvcElement);

        const { error: cardNumberError } = await stripe.createToken(cardNumber);
        const { error: cardExpiryError } = await stripe.createToken(cardExpiry);
        const { error: cardCvcError } = await stripe.createToken(cardCvc);

        if (cardNumberError || cardExpiryError || cardCvcError) {
          toast.error(
            cardNumberError.message ||
              cardExpiryError.message ||
              cardCvcError.message
          );
          return;
        }
        // Create a token with Stripe
        token = await stripe.createToken(
          elements.getElement(CardNumberElement)
        );

        if (!token) {
          toast.error("Error creating token.");
          return;
        }
      }

      const encryptedData = encryptData({
        ...values,
        month: token ? token.token.card.exp_month : billingData.exp_month,
        year: token ? token.token.card.exp_year : billingData.exp_year,
        accountCardEntry: token ? token.token.card.last4 : billingData.last4,
        token_id: token ? token.token.id : billingData.card_id,
      });

      const response = await billingInformationApiAdmin(
        encryptedData,
        providerId,
        headers
      );
      toast.success(response.data.message);
      let obj = {
        event: `SuperAdmin has updated Account Management / Billing Information for loction ${loc.organization_name}.`,
      };

      await createAuditLogs({ data: encryptData(obj) }, headers);
      setButtonText("Saved");
    } 
    catch (error) {
      console.log(error);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
    validateOnChange: true,
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
  } = formik;

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        const response = await getBillingInformationApi(providerId);

        const decryptedData = decryptData(response.data.data);
        setBillingData(decryptedData);
        setValues({
          // paymentMethod: decryptedData.paymentMethod || "",
          accountCardEntry: decryptedData.accountCardEntry || "",
          emailForReceipts: decryptedData.emailForReceipts || "",
          nameOnAccount: decryptedData.nameOnAccount || "",
          postal_code: decryptedData.postal_code || "",
          billingAddress: {
            contactName: decryptedData.billingAddress?.contactName || "",
            billingEmail: decryptedData.billingAddress?.billingEmail || "",
            officeName: decryptedData.billingAddress?.officeName || "",
            streetAddress: decryptedData.billingAddress?.streetAddress || "",
            city: decryptedData.billingAddress?.city || "",
            state: decryptedData.billingAddress?.state || "",
            postalCode: decryptedData.billingAddress?.postalCode || "",
            country: decryptedData.billingAddress?.country || "",
          },
        });
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchBillingData();
  }, [providerId, setValues]);

  // Get options for countries, states, and cities
  const billingAddress = values.billingAddress || {};
  const countries = Country.getAllCountries();
  const states = billingAddress.country
    ? State.getStatesOfCountry(billingAddress.country)
    : [];
  const cities = billingAddress.state
    ? City.getCitiesOfState(billingAddress.country, billingAddress.state)
    : [];

  const handleFieldChange = (e) => {
    handleChange(e);
    setButtonText("Save Changes"); // Change button text when a field is modified
  };

  return (
    <div className="panel-box">
      <form className="sectionalForms" onSubmit={handleSubmit}>
        <div className="row">
          <div className="subtitle">Credit Card Information</div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              {!billingData.last4 ? (
                <CardNumberElement className="StripeElement" />
              ) : (
                <TextField
                  label="Credit Card Number"
                  className="w-100"
                  variant="outlined"
                  size="small"
                  value={`xxxx-xxxx-xxxx-${billingData?.last4}`}
                />
              )}
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                name="emailForReceipts"
                label="Email for Payment Receipts"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.emailForReceipts}
                onChange={handleFieldChange}
                helperText={touched.emailForReceipts && errors.emailForReceipts}
                error={touched.emailForReceipts && !!errors.emailForReceipts}
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                name="nameOnAccount"
                label="Name on the Card"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.nameOnAccount}
                onChange={handleFieldChange}
                helperText={touched.nameOnAccount && errors.nameOnAccount}
                error={touched.nameOnAccount && !!errors.nameOnAccount}
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              {!billingData.exp_month ? (
                <CardExpiryElement className="StripeElement" />
              ) : (
                <TextField
                  label="Card Expiry"
                  className="w-100"
                  variant="outlined"
                  size="small"
                  value={`${billingData?.exp_month}/${billingData?.exp_year}`}
                />
              )}
            </div>
          </div>
          {!billingData.exp_month && (
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="formdesign">
                <CardCvcElement className="StripeElement" />
              </div>
            </div>
          )}
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                name="postal_code"
                label="Credit card Postal Code"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.postal_code}
                onChange={handleFieldChange}
                helperText={touched.postal_code && errors.postal_code}
                error={touched.postal_code && !!errors.postal_code}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="subtitle">Billing Address</div>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                name="billingAddress.contactName"
                label="Billing Contact Name"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.billingAddress.contactName}
                onChange={handleFieldChange}
                helperText={
                  touched.billingAddress?.contactName &&
                  errors.billingAddress?.contactName
                }
                error={
                  touched.billingAddress?.contactName &&
                  !!errors.billingAddress?.contactName
                }
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                name="billingAddress.billingEmail"
                label="Billing Contact Email(Email for Payment Receipts)"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.billingAddress.billingEmail}
                onChange={handleFieldChange}
                helperText={
                  touched.billingAddress?.billingEmail &&
                  errors.billingAddress?.billingEmail
                }
                error={
                  touched.billingAddress?.billingEmail &&
                  !!errors.billingAddress?.billingEmail
                }
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                name="billingAddress.officeName"
                label="Office Name"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.billingAddress.officeName}
                onChange={handleFieldChange}
                helperText={
                  touched.billingAddress?.officeName &&
                  errors.billingAddress?.officeName
                }
                error={
                  touched.billingAddress?.officeName &&
                  !!errors.billingAddress?.officeName
                }
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                name="billingAddress.streetAddress"
                label="Street Address"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.billingAddress.streetAddress}
                onChange={handleFieldChange}
                helperText={
                  touched.billingAddress?.streetAddress &&
                  errors.billingAddress?.streetAddress
                }
                error={
                  touched.billingAddress?.streetAddress &&
                  !!errors.billingAddress?.streetAddress
                }
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <Autocomplete
                options={cities}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name
                }
                value={
                  cities.find((c) => c.name === values.billingAddress.city) ||
                  values.billingAddress.city ||
                  ""
                }
                onChange={(event, newValue) => {
                  if (typeof newValue === "string") {
                    // If the user types a new city, set it as the value
                    setFieldValue("billingAddress.city", newValue);
                  } else {
                    // If the user selects from options, set the selected city's name
                    setFieldValue(
                      "billingAddress.city",
                      newValue ? newValue.name : ""
                    );
                    setButtonText("Save Changes");
                  }
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter((option) =>
                    option.name
                      .toLowerCase()
                      .includes(params.inputValue.toLowerCase())
                  );

                  // Add user input as an option if it doesn't match existing cities
                  if (
                    params.inputValue !== "" &&
                    !filtered.some(
                      (option) => option.name === params.inputValue
                    )
                  ) {
                    filtered.push({
                      name: params.inputValue,
                    });
                  }

                  return filtered;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City"
                    size="small"
                    fullWidth
                    error={
                      touched.billingAddress?.city &&
                      !!errors.billingAddress?.city
                    }
                    helperText={
                      touched.billingAddress?.city &&
                      errors.billingAddress?.city
                    }
                  />
                )}
                freeSolo
                isOptionEqualToValue={(option, value) => option.name === value}
              />
            </div>
          </div>

          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <Autocomplete
                options={states}
                getOptionLabel={(option) => option.name}
                value={
                  states.find(
                    (s) => s.isoCode === values.billingAddress.state
                  ) || null
                }
                onChange={(event, newValue) => {
                  setFieldValue(
                    "billingAddress.state",
                    newValue ? newValue.isoCode : ""
                  );
                  setFieldValue("billingAddress.city", ""); // Reset city when state changes
                  setButtonText("Save Changes");

                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    size="small"
                    fullWidth
                    error={
                      touched.billingAddress?.state &&
                      !!errors.billingAddress?.state
                    }
                    helperText={
                      touched.billingAddress?.state &&
                      errors.billingAddress?.state
                    }
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.isoCode === value
                }
              />
            </div>
          </div>

          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <Autocomplete
                options={countries}
                getOptionLabel={(option) => option.name}
                value={
                  countries.find(
                    (c) => c.isoCode === values.billingAddress.country
                  ) || null
                }
                onChange={(event, newValue) => {
                  setFieldValue(
                    "billingAddress.country",
                    newValue ? newValue.isoCode : ""
                  );
                  setFieldValue("billingAddress.state", "");
                  setFieldValue("billingAddress.city", "");
                  setButtonText("Save Changes");

                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    size="small"
                    fullWidth
                    error={
                      touched.billingAddress?.country &&
                      !!errors.billingAddress?.country
                    }
                    helperText={
                      touched.billingAddress?.country &&
                      errors.billingAddress?.country
                    }
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.isoCode === value
                }
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                name="billingAddress.postalCode"
                label="Postal Code"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.billingAddress.postalCode}
                onChange={handleFieldChange}
                helperText={
                  touched.billingAddress?.postalCode &&
                  errors.billingAddress?.postalCode
                }
                error={
                  touched.billingAddress?.postalCode &&
                  !!errors.billingAddress?.postalCode
                }
                // disabled={enableField}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12 col-md-12 col-sm-12 d-flex align-items-center justify-content-between">
            <div className="text-start">
            </div>
            <div className="text-end">
              
              <div className="text-end modalactions">
               
                <button type="submit" className="btn btn-blacked">
                  {buttonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
